import { FC, JSX, useContext, useEffect, useId, useMemo, useRef, useState } from "react";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { AccordionCollapseContext } from "@app/shared/AccordionsCollapseContext";
import { QESButton } from "@app/shared/qes-standards/Button";
import { expandCollapse } from "@app/shared/qes-standards/utility/expandCollapse";

export interface AccordionProps {
  children?: React.ReactNode;
  /**
   * An optional classname to add to the outermost div in the component
   */
  className?: string;
  /**
   * Determines whether the accordion is initially rendered opened or closed
   */
  defaultOpen?: boolean;
  externalOpen?: boolean;
  /**
   * Optional element rendered in the header alongside the toggle button (e.g. a Save Filters action).
   * Only rendered when provided; existing accordion layouts are unaffected when absent.
   */
  headerAction?: React.ReactNode;
  /**
   * Optional element rendered in the header immediately BEFORE the toggle button.
   *
   * Deliberately a separate slot from `headerAction` rather than a reuse of it:
   *  1. `headerAction` renders after the button, and its existing consumers (EcdFiltersOverview and
   *     QesActiveFiltersBar) rely on that trailing position — repurposing it would shift their controls.
   *  2. A leading control has to be first in the DOM, not merely moved there with CSS `order`, so that
   *     keyboard focus order and screen-reader order match the visual order (WCAG 2.4.3 / 1.3.2).
   *
   * Like `headerAction`, this renders OUTSIDE the toggle button, so an interactive control placed here
   * cannot bubble a click into the expand/collapse handler.
   */
  headerLeading?: React.ReactNode;
  /**
   * If true, the component won't load its children until the first time it's opened. Useful if the children require a lot of processing or API calls for something that might not be used/seen.
   */
  lazyLoad?: boolean;
  /**
   * So QA can do automation testing!
   */
  testId: string;
  /**
   * The title displayed on the always-visible part of the accordion
   */
  title: string | JSX.Element;
  /**
   * An optional disable
   */
  disabled?: boolean;
  /**
   * Visual style variant. "flat" removes card chrome and shows a separator line with Show/Hide text.
   */
  variant?: "default" | "flat";
  /**
   * A function to run before the state update to open the accordion
   */
  onPanelWillOpen?: () => void;
  /**
   * A function to run after the state update to open the accordion
   */
  onPanelDidOpen?: () => void;
  /**
   * A function to run before the state update to close the accordion
   */
  onPanelWillClose?: () => void;
  /**
   * A function to run after the state update to close the accordion
   */
  onPanelDidClose?: () => void;
}

/**
 * Accordion component, used to dynamically show or hide content. The "hidden" content is always 
 * visible in the DOM, unless the lazyLoad prop is true and the accordion has not been opened yet.
 *  <br>
 *  The functionality of closing other accordions when one is opened relies on having an ancestor AccordionContainer.
 */
export const Accordion: FC<AccordionProps> = (props: AccordionProps) => {
  // if there's no ancestor AccordionCollapseContext, this returns an empty object ({})
  const accordionContext = useContext(AccordionCollapseContext);
  // we want this component to function with or without an ancestor AccordionContainer
  const [isOpenState, setIsOpenState] = useState(false);
  const accordionRef = useRef<HTMLDivElement>(null);
  const panelId = useId();
  const collapsingRef = useRef<HTMLDivElement>(null);
  const accordionId = useId();
  const hasOpened = useRef(false);

  useEffect(() => {
    const contextOpen = accordionContext?.expandedAccordion === panelId;
    if (accordionContext.expandedAccordion !== undefined && contextOpen !== isOpenState) {
      setIsOpenState(contextOpen);
    }
  }, [accordionContext?.expandedAccordion]);

  const isOpen = useMemo(() => {
    return accordionContext.expandedAccordion === panelId || isOpenState;
  }, [accordionContext?.expandedAccordion, isOpenState]);

  useEffect(() => {
    if (isOpen) {
      if (props.onPanelDidOpen) {
        props.onPanelDidOpen();
      }
    } else if (hasOpened.current) {
      if (props.onPanelDidClose) {
        props.onPanelDidClose();
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (hasOpened.current) {
      expandCollapse(collapsingRef, props.onPanelWillClose, props.onPanelWillOpen);
    }
  }, [isOpen]);

  useEffect(() => {
    if (props.defaultOpen) {
      onOpenAccordion();
    } else {
      if (isOpen) {
        onCloseAccordion();
      }
    }
  }, [props.defaultOpen]);

  const onCloseAccordion = () => {
    if (accordionContext.setExpandedAccordion) {
      accordionContext?.setExpandedAccordion("");
    }
    setIsOpenState(false);
  }

  const onOpenAccordion = () => {
    hasOpened.current = true;
    if (accordionContext.setExpandedAccordion) {
      accordionContext?.setExpandedAccordion(panelId);
    }
    setIsOpenState(true);
  }

  const onClick = () => {
    if (isOpen) {
      onCloseAccordion();
    } else {
      onOpenAccordion();
    }
  }

  return (
    <div id={accordionId} className={`qes-accordion ${props.variant === "flat" ? "qes-accordion--flat" : ""} ${props.className || ""}`} ref={accordionRef}>
      <div className="qes-accordion__header" id={`${panelId}-header`}>
        {props.headerLeading}
        <QESButton
          qesType="secondary"
          className="qes-accordion__button"
          aria-expanded={isOpen}
          aria-controls={panelId}
          qesTestId={props.testId}
          disabled={props.disabled ?? false}
          aria-pressed={isOpen}
          onClick={onClick}
        >
          <span>{props.title}</span>
          {props.variant === "flat" && <span className="qes-accordion__separator" />}
          <span className="collapse-indicator">
            {props.variant === "flat" && (
              <span className="collapse-indicator__label" aria-hidden="true">{isOpen ? "Hide" : "Show"}</span>
            )}
            <QesIcon iconName={QesIcons.ChevronDown} description={isOpen ? "Collapse" : "Expand"} width="24" />
          </span>
        </QESButton>
        {props.headerAction}
      </div>

      <div
        ref={collapsingRef}
        className={`qes-accordion__body-wrapper qes-js-closed`}
        aria-labelledby={`${panelId}-header`}
      >
        <div className="qes-accordion__body">
          <div className="row">
            <div className="col-12">
              {!props.lazyLoad || hasOpened ? props.children : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

